
import { useState } from 'react';
import { KnowledgeEntryDialog } from "./KnowledgeEntryDialog";
import { KnowledgeSearch } from "./KnowledgeSearch";
import { useKnowledgeBase } from "./useKnowledgeBase";
import { KnowledgeEntry, SearchOptions } from "./types";
import { EntriesList } from "./list-sections/EntriesList";
import { DeleteConfirmationDialog } from "./dialog-sections/DeleteConfirmationDialog";

export function KnowledgeList() {
  const { entries, isLoading, isDeleting, isSearching, handleSearch, handleDelete, invalidateEntries } = useKnowledgeBase();
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<KnowledgeEntry | null>(null);

  if (isLoading) {
    return <div>Loading entries...</div>;
  }

  const handleDeleteRequest = (entry: KnowledgeEntry) => {
    console.log('Delete requested for entry:', entry.id, entry.title);
    setEntryToDelete(entry);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!entryToDelete) {
      console.log('No entry to delete, aborting');
      return;
    }
    
    console.log('Delete confirmed for entry:', entryToDelete.id, entryToDelete.title);
    try {
      console.log('Calling handleDelete');
      await handleDelete(entryToDelete);
      console.log('handleDelete completed successfully');
      setEntryToDelete(null); // Close dialog after successful deletion
    } catch (error) {
      console.error("Error deleting entry:", error);
    }
  };

  // Create wrapper function for handleSearch that matches what KnowledgeSearch expects
  const handleSearchWrapper = async (query: string, options?: SearchOptions) => {
    return await handleSearch(query, options);
  };

  return (
    <div className="space-y-4">
      <KnowledgeSearch onSearch={handleSearchWrapper} isSearching={isSearching} />
      <div className="mt-6">
        <EntriesList
          entries={entries}
          onView={setSelectedEntry}
          onDelete={handleDeleteRequest}
        />
      </div>

      {selectedEntry && (
        <KnowledgeEntryDialog
          entry={selectedEntry}
          isOpen={!!selectedEntry}
          onOpenChange={(open) => {
            if (!open) setSelectedEntry(null);
          }}
          onUpdate={invalidateEntries}
        />
      )}

      <DeleteConfirmationDialog
        isOpen={!!entryToDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            console.log('Delete dialog closed without confirmation');
            setEntryToDelete(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        title="Delete Knowledge Entry"
        description="Are you sure you want to delete this knowledge entry? This action cannot be undone."
      />
    </div>
  );
}
